import { DateTime } from 'luxon';
import { Valid } from 'luxon/src/_util';
import { Logger } from '../../utils/logger';
import { KV, Entry, minutesToMillis, Entries } from '.';

export default class LocalStorageKV implements KV {
	key: string;
	store: Map<string, Entry>;
	timeoutStore: Map<string, any>;
	DEFAULT_TTL = 10;
	logger: Logger;

	/**
	 * Creates a Key-Value Store using localStorage
	 * as a persistent layer
	 *
	 * @param key - Is the key in localStorage
	 *
	 */
	constructor(key: string) {
		this.key = key;
		this.logger = new Logger('KV', {
			log: 'rgb(50, 173, 230)',
			debug: 'rgb(0, 113, 164)',
			info: 'rgb(112, 215, 255)',
		});
		if (!localStorage.getItem(key)) {
			localStorage.setItem(key, '{}');
		}
		this.timeoutStore = new Map();
		this.store = new Map();

		const now = DateTime.now();
		const cacheEntries = this.loadCachedEntries(now);
		for (const k in cacheEntries) {
			const { ttl, value } = cacheEntries[k];
			if (ttl === null) {
				this.store.set(k, {
					ttl,
					value,
				});
				continue;
			}
			this.onLoadSet(k, value, DateTime.fromJSDate(ttl), now);
		}
		this.save();
	}

	has(key: string): boolean {
		return this.store.has(key);
	}

	get(key: string, defaultValue?: any) {
		const entry = this.store.get(key);
		if (!entry && defaultValue) {
			return defaultValue;
		}
		if (!entry) {
			return null;
		}
		return entry.value;
	}

	set(key: string, data: any, ttl?: number | null) {
		// Check if there is a key and if it's attached to a timeout
		// If it is, it clears that timeout so it can be overwriten
		if (this.store.has(key) && this.timeoutStore.has(key)) {
			const id = this.timeoutStore.get(key);
			clearTimeout(id);
		}
		if (ttl === null) {
			this.logger.info(
				`Set key "${key}" without ttl with value "${data}"`,
			);
			this.store.set(key, {
				value: data,
				ttl,
			});
			this.save();
			return;
		}

		let milliseconds = minutesToMillis(this.DEFAULT_TTL);

		if (ttl) {
			milliseconds = minutesToMillis(ttl);
		}

		// Get the timestamp when saving the item
		const now = DateTime.now();
		const ttlDT = now.plus({ milliseconds });
		this.logger.info(
			`Set the key "${key}" with value "${data}" to expire in ${msToHMS(
				milliseconds,
			)} minutes. (${ttlDT.toISO()})`,
		);
		this.store.set(key, {
			value: data,
			ttl: ttlDT.toJSDate(),
		});
		const timeoutId = setTimeout(() => {
			this.logger.info(`Delete the key: "${key}"`);
			this.delete(key);
			if (this.timeoutStore.get(key)) {
				this.timeoutStore.delete(key);
			}
		}, milliseconds);

		this.timeoutStore.set(key, timeoutId);

		this.save();
	}

	delete(key: string) {
		if (this.has(key)) {
			this.store.delete(key);
			const id = this.timeoutStore.get(key);
			if (id) {
				clearTimeout(id);
			}
		}
		this.save();
	}

	clear() {
		this.store.clear();
		for (const [k] of this.timeoutStore) {
			const id = this.timeoutStore.get(k);
			if (id) {
				clearTimeout(id);
			}
		}
		this.timeoutStore.clear();
		this.save();
	}

	/**
	 * Stores the values from memory into localStorage
	 *
	 */
	private save() {
		const data: Entries = {};
		for (const [key, entry] of this.store) {
			const { value, ttl } = entry;
			data[key] = {
				value,
				ttl,
			};
		}

		localStorage.setItem(this.key, JSON.stringify(data));
	}

	/**
	 * Return a LocalEntry object with the data stored in localStorage
	 *
	 * @param {DateTime} now - Current time
	 *
	 */
	private loadCachedEntries(now: DateTime<Valid>): Entries {
		const entries: Entries = {};
		const item = localStorage.getItem(this.key);
		const localEntries = item ? (JSON.parse(item) as LocalEntries) : {};
		for (const k in localEntries) {
			const { value, ttl } = localEntries[k];
			if (ttl === null) {
				entries[k] = {
					value,
					ttl,
				};
				continue;
			}

			const ttlDT = DateTime.fromJSDate(new Date(ttl));
			// If the ttl already expired I don't load it
			if (now >= ttlDT) {
				continue;
			}
			entries[k] = {
				value: value,
				ttl: ttlDT.toJSDate(),
			};
		}

		return entries;
	}

	/**
	 * Start loading the timeout callbacks based on the items that have
	 * a TTL set
	 *
	 * @param {string} key - Loookup Key
	 * @param {any} data - The value that you want to store
	 * @param {DateTime} ttl - The absolute expiration date of the item
	 * @param {DateTime} now - Current time
	 *
	 */
	private onLoadSet(key: string, data: any, ttl: DateTime, now: DateTime) {
		const diff = ttl.diff(now).toMillis();
		this.store.set(key, {
			ttl: ttl.toJSDate(),
			value: data,
		});
		const timeoutId = setTimeout(() => {
			this.delete(key);
		}, diff);
		this.timeoutStore.set(key, timeoutId);
	}
}

interface LocalEntries {
	[key: string]: LocalEntry;
}

interface LocalEntry {
	value: any;
	ttl: string | null;
}

function msToHMS(ms: number) {
	// 1- Convert to seconds:
	let seconds = ms / 1000;
	// 2- Extract hours:
	//const hours = parseInt(`${seconds / 3600}`); // 3,600 seconds in 1 hour
	seconds = seconds % 3600; // seconds remaining after extracting hours
	// 3- Extract minutes:
	const minutes = parseInt(`${seconds / 60}`); // 60 seconds in 1 minute
	// 4- Keep only seconds not extracted to minutes:
	//seconds = seconds % 60;
	return minutes;

	// return [hours, minutes, seconds].join(':');
}
